import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useOutletContext } from 'react-router-dom';
import axios from 'axios';
import { useQueryClient } from '@tanstack/react-query';
import { SimpleMenu } from '@/components/MenuGrid';
import { useLocale } from '@/context/LocaleContext';
import {
  useConversations,
  useMessageInventory,
  useMarkRead,
  useOnlinePlayers,
  refetchConversationsList,
  refreshMessagesUnreadCount,
  type Conversation,
  type MessageInventoryItem,
  type OnlinePlayer,
} from '@/hooks/useMessages';
import {
  checkUserStatus,
  sendTyping,
  subscribeMessagesWs,
  useMessagesWebSocketSync,
} from '@/hooks/useMessagesWebSocket';
import type { AuthUser, PlayerData } from '@/vite-env.d';
import { ConversationList } from '@/pages/messages/ConversationList';
import { type AttachedItem } from '@/pages/messages/MessageInput';
import { MessageInventoryPicker } from '@/pages/messages/MessageInventoryPicker';
import { OnlinePlayersModal } from '@/pages/messages/OnlinePlayersList';
import { ChatSession } from '@/pages/messages/ChatSession';
import '@/pages/messages/messages.css';

interface ActiveChat {
  charId: number;
  charName: string;
  refObjId: number;
}

interface MessagesOpenChatState {
  openChat?: ActiveChat;
}

export function MessagesPage() {
  const { t } = useLocale();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { user } = useOutletContext<{ user: AuthUser; player: PlayerData | null }>();
  const currentCharId = user.charId;

  const {
    data: conversations = [],
    isPending: convPending,
    isError: convIsError,
    error: convErrorObj,
    dataUpdatedAt: convUpdatedAt,
    refetch: refetchConversations,
  } = useConversations(currentCharId);
  const markReadMutation = useMarkRead(currentCharId);

  // Diagnóstico visible: distinguir "lista vacía" de "la API falló" o "sesión sin charId".
  const convLoadError = useMemo(() => {
    if (currentCharId <= 0) return `${t('messages_error_load')} (auth: charId=0)`;
    if (!convIsError) return null;
    if (axios.isAxiosError(convErrorObj)) {
      const status = convErrorObj.response?.status;
      return `${t('messages_error_load')} (${status ? `HTTP ${status}` : 'network'})`;
    }
    return t('messages_error_load');
  }, [currentCharId, convIsError, convErrorObj, t]);

  const [activeChat, setActiveChat] = useState<ActiveChat | null>(null);
  const [partnerStatus, setPartnerStatus] = useState<'online' | 'offline'>('offline');
  const [isTyping, setIsTyping] = useState(false);
  const [showPlayersModal, setShowPlayersModal] = useState(false);
  const [showInventoryModal, setShowInventoryModal] = useState(false);
  const [playerSearch, setPlayerSearch] = useState('');
  const [attachedByChat, setAttachedByChat] = useState<Record<number, AttachedItem>>({});

  useEffect(() => {
    if (currentCharId <= 0) return;
    void refetchConversationsList(queryClient, currentCharId);
    void refreshMessagesUnreadCount(queryClient, currentCharId);
  }, [currentCharId, queryClient, location.key]);

  useEffect(() => {
    const openChat = (location.state as MessagesOpenChatState | null)?.openChat;
    if (!openChat?.charId) return;

    setActiveChat(openChat);
    setPartnerStatus('offline');
    setIsTyping(false);
    window.history.replaceState({}, '', location.pathname);
  }, [location.state, location.pathname]);

  const displayConversations = useMemo(() => {
    if (!activeChat) return conversations;
    if (conversations.some((c) => c.otherCharID === activeChat.charId)) return conversations;

    const placeholder: Conversation = {
      id: 0,
      sender_CharID: currentCharId,
      receiver_CharID: activeChat.charId,
      message: '',
      status: 'read',
      created_at: new Date().toISOString().slice(0, 19).replace('T', ' '),
      otherCharName: activeChat.charName,
      otherRefObjID: activeChat.refObjId,
      otherCharID: activeChat.charId,
      unreadCount: 0,
    };
    return [placeholder, ...conversations];
  }, [conversations, activeChat, currentCharId]);

  const otherCharId = activeChat?.charId ?? null;
  const attachedItem = otherCharId ? (attachedByChat[otherCharId] ?? null) : null;

  const setAttachedItemForChat = useCallback((charId: number, item: AttachedItem | null) => {
    setAttachedByChat((prev) => {
      if (!item) {
        const next = { ...prev };
        delete next[charId];
        return next;
      }
      return { ...prev, [charId]: item };
    });
  }, []);

  const { data: onlinePlayers = [], isLoading: playersLoading } = useOnlinePlayers(
    showPlayersModal ? playerSearch : '',
  );
  const { data: inventoryItems = [], isLoading: inventoryLoading } = useMessageInventory(showInventoryModal);

  useMessagesWebSocketSync(otherCharId, currentCharId);

  useEffect(() => {
    if (!otherCharId) return;
    checkUserStatus(otherCharId);
  }, [otherCharId]);

  useEffect(() => {
    let typingTimer: ReturnType<typeof setTimeout> | null = null;

    const unsubscribe = subscribeMessagesWs((event) => {
      if (event.type === 'user_status' && activeChat && event.charID === activeChat.charId) {
        setPartnerStatus(event.status);
      }
      if (event.type === 'typing' && activeChat && event.sender === activeChat.charId) {
        setIsTyping(true);
        if (typingTimer) clearTimeout(typingTimer);
        typingTimer = setTimeout(() => setIsTyping(false), 3000);
      }
    });

    return () => {
      unsubscribe();
      if (typingTimer) clearTimeout(typingTimer);
    };
  }, [activeChat]);

  const openConversation = useCallback((conv: Conversation) => {
    setActiveChat({
      charId: conv.otherCharID,
      charName: conv.otherCharName,
      refObjId: conv.otherRefObjID,
    });
    setPartnerStatus('offline');
    setIsTyping(false);
    if (conv.unreadCount > 0) {
      markReadMutation.mutate(conv.otherCharID);
    }
  }, [markReadMutation]);

  const openPlayer = useCallback((player: OnlinePlayer) => {
    setActiveChat({
      charId: player.CharID,
      charName: player.CharName16,
      refObjId: player.RefObjID,
    });
    setShowPlayersModal(false);
    setPlayerSearch('');
    setPartnerStatus('offline');
    setIsTyping(false);
    void refetchConversations();
  }, [refetchConversations]);

  const handleInventorySelect = (item: MessageInventoryItem) => {
    if (!otherCharId) return;
    setAttachedItemForChat(otherCharId, { id64: item.id64, name: item.name, icon: item.icon });
    setShowInventoryModal(false);
  };

  return (
    <div className="container-ingame messages-page">
      <div className="calendar-container">
        <SimpleMenu title={t('messages')} />

        <div className="chat-main-container">
          <div className="chat-container">
            <ConversationList
              conversations={displayConversations}
              activeCharId={otherCharId}
              isLoading={currentCharId > 0 && convPending && conversations.length === 0}
              loadError={convLoadError}
              onRetry={() => void refetchConversations()}
              retryLabel={t('messages_retry')}
              onSelect={openConversation}
              onNewChat={() => setShowPlayersModal(true)}
              emptyLabel={t('messages_no_conversations')}
              loadingLabel={t('messages_loading')}
              title={t('messages_conversations')}
              newChatTitle={t('messages_new_chat')}
            />

            <div className="chat-window">
              {!activeChat ? (
                <div className="chat-empty-state" id="empty-state">
                  <svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="#666666" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                  </svg>
                  <p>{t('messages_select_player')}</p>
                </div>
              ) : (
                <ChatSession
                  key={activeChat.charId}
                  otherCharId={activeChat.charId}
                  otherCharName={activeChat.charName}
                  otherRefObjId={activeChat.refObjId}
                  currentCharId={currentCharId}
                  attachedItem={attachedItem}
                  onAttachClick={() => setShowInventoryModal(true)}
                  onRemoveAttached={() => setAttachedItemForChat(activeChat.charId, null)}
                  partnerStatus={partnerStatus}
                  isTyping={isTyping}
                />
              )}
            </div>
          </div>
        </div>

        {showPlayersModal ? (
          <OnlinePlayersModal
            players={onlinePlayers}
            isLoading={playersLoading}
            search={playerSearch}
            onSearchChange={setPlayerSearch}
            onSelect={openPlayer}
            loadingLabel={t('messages_loading')}
            searchPlaceholder={t('messages_search_player')}
            title={t('messages_new_chat')}
            onClose={() => {
              setShowPlayersModal(false);
              setPlayerSearch('');
            }}
          />
        ) : null}

        {showInventoryModal ? (
          <MessageInventoryPicker
            items={inventoryItems}
            isLoading={inventoryLoading}
            title={t('messages_attach_item')}
            cancelLabel={t('messages_cancel')}
            loadingLabel={t('messages_loading')}
            onSelect={handleInventorySelect}
            onClose={() => setShowInventoryModal(false)}
          />
        ) : null}
      </div>
    </div>
  );
}
